import { useEffect, useMemo, useState } from 'react';
import {
  Shield, UserPlus, Users, Mail, Phone, Edit2, X, Check,
  UserX, Eye, EyeOff, Search, KeyRound, RefreshCw, Info, Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { portalUserService, permissionRoleService } from '../../services/portalUserService';
import { PERMISSION_MODULES } from '../../utils/constants';
import { UI, ui, buttonStyle, fieldStyles } from '../../utils/ui';

// ─── Styles ───────────────────────────────────────────────────────────────────

const inputRowStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 14px', borderRadius: 14,
  border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff',
};
const inputCore = {
  border: 'none', outline: 'none', flex: 1, fontSize: 14,
  backgroundColor: 'transparent', color: UI.colors.text,
};

const statusPill = (active) => ({
  display: 'inline-flex', alignItems: 'center', padding: '4px 10px',
  borderRadius: 9999, fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap',
  border: `1px solid ${active ? '#86efac' : '#fecaca'}`,
  backgroundColor: active ? '#dcfce7' : '#fee2e2',
  color: active ? '#166534' : '#991b1b',
});

// ─── Permission Summary Chip ──────────────────────────────────────────────────

const PermissionSummary = ({ roleName, roleColor, permissions }) => {
  if (!roleName) return <span style={{ color: UI.colors.muted, fontSize: 12 }}>No role assigned</span>;

  const totalPerms = Object.values(permissions || {}).reduce(
    (s, a) => s + (Array.isArray(a) ? a.length : 0), 0
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 9999,
        border: `1.5px solid ${roleColor || '#6b7280'}40`,
        backgroundColor: (roleColor || '#6b7280') + '15',
        fontSize: 12, fontWeight: 700,
        color: roleColor || '#6b7280',
      }}>
        <Shield size={11} />
        {roleName}
      </span>
      <span style={{ fontSize: 11, color: UI.colors.muted }}>({totalPerms} perms)</span>
    </div>
  );
};

// ─── Create / Edit Portal User Modal ─────────────────────────────────────────

const PortalUserModal = ({ editingUser, roles, onClose, onSaved }) => {
  const isEdit = Boolean(editingUser);

  const [form, setForm] = useState({
    name: editingUser?.name || '',
    email: editingUser?.email || '',
    phone: editingUser?.phone || '',
    password: '',
    role_id: editingUser?.role_id ? String(editingUser.role_id) : '',
    is_active: editingUser?.is_active !== false,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const selectedRole = roles.find((r) => String(r.id) === String(form.role_id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required.');
    if (!form.email.trim()) return toast.error('Email is required.');
    if (!isEdit && !form.password) return toast.error('Password is required.');
    if (!isEdit && form.password.length < 6) return toast.error('Password must be at least 6 characters.');
    if (!form.role_id) return toast.error('Please assign a permission role.');

    setSaving(true);
    try {
      let result;
      if (isEdit) {
        const payload = {
          name: form.name.trim(),
          phone: form.phone.trim() || undefined,
          role_id: parseInt(form.role_id),
          is_active: form.is_active,
        };
        result = await portalUserService.update(editingUser.id, payload);
        toast.success(`${result.data.portal_user.name} updated.`);
      } else {
        result = await portalUserService.create({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          password: form.password,
          role_id: parseInt(form.role_id),
        });
        toast.success(`Portal user "${result.data.portal_user.name}" created!`);
      }
      onSaved(result.data.portal_user, isEdit);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save portal user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{
        ...ui.card, position: 'relative', width: '100%', maxWidth: 500, zIndex: 1,
        padding: 0, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${UI.colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h2 style={{ fontFamily: UI.font.heading, fontSize: 22, fontWeight: 900, margin: 0, color: UI.colors.text }}>
              {isEdit ? 'Edit Portal User' : 'Create Portal User'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: UI.colors.muted }}>
              {isEdit ? 'Update staff account details and role' : 'Add a new staff member to the owner portal'}
            </p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: UI.colors.muted, display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Name */}
          <div>
            <label style={fieldStyles.label}>Full Name *</label>
            <div className="input-row" style={inputRowStyle}>
              <Users size={16} color={form.name ? UI.colors.primary : '#9ca3af'} />
              <input type="text" placeholder="Staff member's full name" value={form.name} onChange={(e) => set('name', e.target.value)} style={inputCore} required />
            </div>
          </div>

          {/* Email */}
          <div>
            <label style={fieldStyles.label}>Email Address *</label>
            <div className="input-row" style={{ ...inputRowStyle, opacity: isEdit ? 0.6 : 1 }}>
              <Mail size={16} color={form.email ? UI.colors.primary : '#9ca3af'} />
              <input
                type="email"
                placeholder="staff@buranshikes.com"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                style={inputCore}
                required
                disabled={isEdit}
              />
            </div>
            {isEdit && <p style={fieldStyles.help}>Email cannot be changed after creation.</p>}
          </div>

          {/* Phone */}
          <div>
            <label style={fieldStyles.label}>Phone <span style={{ color: UI.colors.muted, fontWeight: 400 }}>(optional)</span></label>
            <div className="input-row" style={inputRowStyle}>
              <Phone size={16} color={form.phone ? UI.colors.primary : '#9ca3af'} />
              <input type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={(e) => set('phone', e.target.value)} style={inputCore} />
            </div>
          </div>

          {/* Password (create only) */}
          {!isEdit && (
            <div>
              <label style={fieldStyles.label}>Password *</label>
              <div className="input-row" style={inputRowStyle}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Minimum 6 characters"
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                  style={inputCore}
                  required
                />
                <button type="button" onClick={() => setShowPwd((p) => !p)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: '#9ca3af', display: 'flex', flexShrink: 0 }}>
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}

          {/* Permission Role */}
          <div>
            <label style={fieldStyles.label}>Permission Role *</label>
            {roles.length === 0 ? (
              <div style={{ padding: '12px 14px', borderRadius: 14, backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
                <p style={{ margin: 0, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                  No permission roles found. Please create a role first in "Permission Roles".
                </p>
              </div>
            ) : (
              <>
                <select
                  value={form.role_id}
                  onChange={(e) => set('role_id', e.target.value)}
                  style={{ ...fieldStyles.input, cursor: 'pointer' }}
                  required
                >
                  <option value="">— Select a role —</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>

                {/* Role preview */}
                {selectedRole && (
                  <div style={{ marginTop: 8, padding: '10px 14px', borderRadius: 12, backgroundColor: selectedRole.color + '10', border: `1px solid ${selectedRole.color}30` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <Shield size={14} color={selectedRole.color} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: selectedRole.color }}>{selectedRole.name}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {Object.entries(selectedRole.permissions || {}).filter(([, a]) => a.length > 0).map(([mod]) => (
                        <span key={mod} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999, backgroundColor: selectedRole.color + '20', color: selectedRole.color }}>
                          {PERMISSION_MODULES[mod]?.label || mod}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Active toggle (edit only) */}
          {isEdit && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#f9fafb' }}>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: UI.colors.text }}>Account Status</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: UI.colors.muted }}>Inactive users cannot log in</p>
              </div>
              <button
                type="button"
                onClick={() => set('is_active', !form.is_active)}
                style={{
                  width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                  backgroundColor: form.is_active ? UI.colors.primary : '#d1d5db',
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', backgroundColor: '#fff',
                  position: 'absolute', top: 3, left: form.is_active ? 23 : 3,
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <button type="button" onClick={onClose} style={{ ...buttonStyle('outline'), flex: 1, padding: '12px 16px', borderRadius: 14 }}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ ...buttonStyle('primary'), flex: 1, padding: '12px 16px', borderRadius: 14, opacity: saving ? 0.75 : 1 }}
            >
              {saving ? (
                <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : (
                <UserPlus size={16} />
              )}
              {saving ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Password Reset Modal ─────────────────────────────────────────────────────

const ResetPasswordModal = ({ user, onClose }) => {
  const [newPwd, setNewPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) return toast.error('Password must be at least 6 characters.');
    setSaving(true);
    try {
      await portalUserService.resetPassword(user.id, newPwd);
      toast.success(`Password reset for ${user.name}.`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{ ...ui.card, position: 'relative', width: '100%', maxWidth: 400, zIndex: 1, padding: 0 }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${UI.colors.border}` }}>
          <h2 style={{ fontFamily: UI.font.heading, fontSize: 20, fontWeight: 900, margin: 0, color: UI.colors.text }}>Reset Password</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: UI.colors.muted }}>Set a new password for {user.name}</p>
        </div>
        <form onSubmit={handleReset} style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={fieldStyles.label}>New Password *</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
              <input
                type={showPwd ? 'text' : 'password'}
                placeholder="Minimum 6 characters"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14, backgroundColor: 'transparent', color: UI.colors.text }}
                autoFocus
              />
              <button type="button" onClick={() => setShowPwd((p) => !p)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: '#9ca3af', display: 'flex' }}>
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ ...buttonStyle('outline'), flex: 1, padding: '12px 16px', borderRadius: 14 }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...buttonStyle('primary'), flex: 1, padding: '12px 16px', borderRadius: 14, opacity: saving ? 0.75 : 1 }}>
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ManagePortalUsers = () => {
  const [portalUsers, setPortalUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [deactivating, setDeactivating] = useState(null);

  useEffect(() => {
    Promise.all([
      portalUserService.getAll(),
      permissionRoleService.getAll(),
    ])
      .then(([usersRes, rolesRes]) => {
        setPortalUsers(usersRes.data.portal_users || []);
        setRoles(rolesRes.data.roles || []);
      })
      .catch(() => toast.error('Failed to load portal users.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSaved = (user, isEdit) => {
    setPortalUsers((prev) =>
      isEdit ? prev.map((u) => u.id === user.id ? { ...u, ...user } : u) : [user, ...prev]
    );
  };

  const handleDeactivate = (user) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 8px', fontWeight: 900, color: UI.colors.text }}>Deactivate {user.name}?</p>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: UI.colors.muted }}>They will lose access to the portal immediately.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              setDeactivating(user.id);
              try {
                await portalUserService.deactivate(user.id);
                setPortalUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, is_active: false } : u));
                toast.success(`${user.name}'s account deactivated.`);
              } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to deactivate.');
              } finally {
                setDeactivating(null);
              }
            }}
            style={{ ...buttonStyle('danger'), padding: '8px 12px', borderRadius: 12 }}
          >
            Deactivate
          </button>
          <button onClick={() => toast.dismiss(t.id)} style={{ ...buttonStyle('outline'), padding: '8px 12px', borderRadius: 12 }}>Cancel</button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return portalUsers.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role_name || '').toLowerCase().includes(q)
    );
  }, [portalUsers, search]);

  const stats = useMemo(() => ({
    total: portalUsers.length,
    active: portalUsers.filter((u) => u.is_active).length,
    roles: new Set(portalUsers.map((u) => u.role_id)).size,
  }), [portalUsers]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showModal && (
        <PortalUserModal
          editingUser={editingUser}
          roles={roles}
          onClose={() => { setShowModal(false); setEditingUser(null); }}
          onSaved={handleSaved}
        />
      )}
      {resetUser && (
        <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text }}>Portal Users</h1>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: UI.colors.muted }}>
            Staff members who can access the owner portal with specific permissions
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditingUser(null); setShowModal(true); }}
          style={{ ...buttonStyle('primary'), padding: '11px 20px', borderRadius: 14 }}
        >
          <UserPlus size={16} /> Add Staff User
        </button>
      </div>

      {/* No roles warning */}
      {roles.length === 0 && (
        <div style={{ padding: '12px 16px', borderRadius: 12, backgroundColor: '#fef3c7', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Info size={16} color="#d97706" />
          <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
            <strong>No permission roles found.</strong> Go to "Permission Roles" to create roles before adding portal users.
          </p>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
        {[
          { label: 'Total Staff', value: stats.total, color: '#7c3aed', bg: 'rgba(124,58,237,0.10)' },
          { label: 'Active', value: stats.active, color: '#16a34a', bg: 'rgba(22,163,74,0.10)' },
          { label: 'Roles Used', value: stats.roles, color: '#ea580c', bg: 'rgba(234,88,12,0.10)' },
        ].map((s) => (
          <div key={s.label} style={{ ...ui.card, padding: 18, textAlign: 'center', backgroundColor: s.bg, borderColor: 'transparent' }}>
            <p style={{ margin: 0, fontFamily: UI.font.heading, fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</p>
            <p style={{ margin: '6px 0 0', fontSize: 11, fontWeight: 900, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
        <Search size={16} color="#9ca3af" />
        <input
          type="text"
          placeholder="Search by name, email or role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={inputCore}
        />
        {search && (
          <button type="button" onClick={() => setSearch('')}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, display: 'flex' }}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Table */}
      <div style={ui.card}>
        <div style={{ padding: 22 }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 18px', color: UI.colors.muted }}>
              <Shield size={44} color="#9ca3af" style={{ opacity: 0.3, margin: '0 auto 12px', display: 'block' }} />
              <p style={{ margin: 0, fontWeight: 900 }}>No portal users found</p>
              <p style={{ margin: '8px 0 0', fontSize: 13 }}>
                {search ? 'Try a different search term.' : 'Click "Add Staff User" to create the first one.'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 700 }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '2fr 2fr 2fr 1fr 1.5fr',
                  gap: 12, padding: '10px 12px', borderRadius: 14,
                  backgroundColor: '#f3f4f6', border: `1px solid ${UI.colors.border}`,
                  fontSize: 11, fontWeight: 900, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10,
                }}>
                  <span>Staff Member</span>
                  <span>Contact</span>
                  <span>Role & Permissions</span>
                  <span>Status</span>
                  <span style={{ textAlign: 'right' }}>Actions</span>
                </div>

                {filtered.map((user) => {
                  const role = roles.find((r) => r.id === user.role_id);
                  return (
                    <div
                      key={user.id}
                      style={{
                        display: 'grid', gridTemplateColumns: '2fr 2fr 2fr 1fr 1.5fr',
                        gap: 12, padding: '14px 12px',
                        borderBottom: `1px solid ${UI.colors.border}`,
                        alignItems: 'center', fontSize: 13,
                      }}
                    >
                      {/* Name + avatar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div style={{
                          width: 38, height: 38, borderRadius: 9999, flexShrink: 0,
                          backgroundColor: (user.role_color || '#6b7280') + '20',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <span style={{ fontWeight: 900, color: user.role_color || '#6b7280', fontSize: 15 }}>
                            {(user.name || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, fontWeight: 800, color: UI.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</p>
                          <p style={{ margin: '2px 0 0', fontSize: 11, color: UI.colors.muted }}>
                            Since {new Date(user.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {/* Contact */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <Mail size={12} color="#9ca3af" />
                          <span style={{ fontSize: 12, color: UI.colors.muted, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</span>
                        </div>
                        {user.phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Phone size={12} color="#9ca3af" />
                            <span style={{ fontSize: 12, color: UI.colors.muted, fontWeight: 600 }}>{user.phone}</span>
                          </div>
                        )}
                      </div>

                      {/* Role */}
                      <PermissionSummary
                        roleName={user.role_name}
                        roleColor={user.role_color}
                        permissions={role?.permissions}
                      />

                      {/* Status */}
                      <span style={statusPill(Boolean(user.is_active))}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>

                      {/* Actions */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                        <button
                          type="button"
                          onClick={() => { setEditingUser(user); setShowModal(true); }}
                          title="Edit user"
                          style={{ ...buttonStyle('outline'), padding: '7px 10px', borderRadius: 10 }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setResetUser(user)}
                          title="Reset password"
                          style={{ ...buttonStyle('outline'), padding: '7px 10px', borderRadius: 10 }}
                        >
                          <RefreshCw size={14} />
                        </button>
                        {user.is_active && (
                          <button
                            type="button"
                            onClick={() => handleDeactivate(user)}
                            disabled={deactivating === user.id}
                            title="Deactivate user"
                            style={{ ...buttonStyle('outline'), padding: '7px 10px', borderRadius: 10, borderColor: '#fecaca', color: UI.colors.danger }}
                          >
                            {deactivating === user.id
                              ? <span style={{ width: 12, height: 12, border: '2px solid #fecaca', borderTopColor: UI.colors.danger, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                              : <UserX size={14} />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagePortalUsers;
